/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ApprovalDecision,
  ApprovalDecision$inboundSchema,
} from "./approvaldecision.js";
import {
  ApprovalRequester,
  ApprovalRequester$inboundSchema,
} from "./approvalrequester.js";
import {
  ApprovalRequestSummary,
  ApprovalRequestSummary$inboundSchema,
} from "./approvalrequestsummary.js";
import { EvidenceDiff, EvidenceDiff$inboundSchema } from "./evidencediff.js";
import {
  ResearchReport,
  ResearchReport$inboundSchema,
} from "./researchreport.js";

/**
 * An approval request with everything needed to decide it.
 */
export type ApprovalRequestDetail = {
  /**
   * Every decision made on this server, newest first. A repeat request starts from the last rationale rather than from zero.
   */
  decisions: Array<ApprovalDecision>;
  /**
   * The deterministic signals gathered for this server, as they stood when last collected. Every item is a declaration by the server or its registry, never an observation of behaviour.
   */
  evidence?: any | undefined;
  /**
   * When the evidence was last gathered.
   */
  evidenceCollectedAt?: Date | undefined;
  /**
   * What moved between the latest decision's evidence snapshot and the current gather, restricted to the permission-relevant slice: OAuth scopes, authority mode, demanded credentials, and published advisories. A change here is a re-review trigger, never a verdict — an unchanged published interface says nothing about unchanged behavior.
   */
  evidenceDiff?: EvidenceDiff | undefined;
  /**
   * Shape version of the evidence payload, so an older snapshot stays interpretable.
   */
  evidenceVersion?: number | undefined;
  /**
   * One MCP server awaiting a decision.
   */
  request: ApprovalRequestSummary;
  /**
   * Everyone who asked.
   */
  requesters: Array<ApprovalRequester>;
  /**
   * Every research-agent run for this request, newest first.
   */
  researchReports: Array<ResearchReport>;
};

/** @internal */
export const ApprovalRequestDetail$inboundSchema: z.ZodMiniType<
  ApprovalRequestDetail,
  unknown
> = z.pipe(
  z.object({
    decisions: z.array(ApprovalDecision$inboundSchema),
    evidence: z.optional(z.any()),
    evidence_collected_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    evidence_diff: z.optional(EvidenceDiff$inboundSchema),
    evidence_version: z.optional(z.int()),
    request: ApprovalRequestSummary$inboundSchema,
    requesters: z.array(ApprovalRequester$inboundSchema),
    research_reports: z.array(ResearchReport$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "evidence_collected_at": "evidenceCollectedAt",
      "evidence_diff": "evidenceDiff",
      "evidence_version": "evidenceVersion",
      "research_reports": "researchReports",
    });
  }),
);

export function approvalRequestDetailFromJSON(
  jsonString: string,
): SafeParseResult<ApprovalRequestDetail, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ApprovalRequestDetail$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ApprovalRequestDetail' from JSON`,
  );
}
